import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import useClientsInfinitScroll from "@/hooks/collectionOrder/useClientsInfinitScroll";
import { ArrowBigDown, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface CustomerDropdownProps {
  addCommandeForm: any;
  isUpdate?: boolean;
  setCustomer: (customer: Client) => void;
}

export const CustomerDropdown: React.FC<CustomerDropdownProps> = ({
  addCommandeForm,
  isUpdate = false,
  setCustomer,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { clients, loading, handleRefresh, loadMore } = useClientsInfinitScroll(
    { searchTerm }
  );

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          scrollContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          loadMore(); // Load more clients when scrolled near the bottom
          console.log("load more");
        }
      }
    };

    const ref = scrollContainerRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (ref) {
        ref.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loadMore]);

  return (
    <div className="relative flex flex-col">
      <div onClick={() => setIsOpen(!isOpen)} className="relative">
        <Input
          disabled={isUpdate}
          type="text"
          id="customerId"
          role="combobox"
          className="pl-3 w-full justify-between rounded-[5px] border font-normal border-[#C9C9C9] bg-white"
          placeholder="Nom Commercial"
          value={
            addCommandeForm.getValues("customerId")
              ? clients.find(
                  (client) =>
                    client.id === addCommandeForm.getValues("customerId")
                )?.commercial_name || ""
              : ""
          }
          onChange={(e) => {
            setSearchTerm(e.target.value);
            handleRefresh(); // Trigger a refresh with the new search term
          }}
          readOnly
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown className="h-6 w-6 opacity-50" />
        </button>
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 bg-white border mt-[50px] w-full rounded-lg shadow-lg"
        >
          <input
            type="text"
            placeholder="Chercher un client..."
            className="h-9 w-full p-2 border-b placeholder:text-sm text-sm"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleRefresh(); // Trigger a refresh with the new search term
            }}
          />
          {clients.length === 0 && !loading && (
            <div className="p-2 text-gray-500 text-sm">
              Pas de client trouvé.
            </div>
          )}
          <div ref={scrollContainerRef} className="h-[200px] overflow-y-scroll">
            {clients.map((client) => (
              <div
                key={client.id}
                className="p-2 hover:bg-gray-100 text-sm cursor-pointer flex justify-between items-center"
                onClick={() => {
                  setCustomer(client);
                  addCommandeForm.setValue("customerId", client.id);
                  setIsOpen(false); // Close dropdown on selection
                }}
              >
                {client.commercial_name}
                {client.id === addCommandeForm.getValues("customerId") && (
                  <span className="text-blue-500">✔</span>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-center p-2">
                <Spinner />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="text-red-500">{/* Error message if any */}</div>
    </div>
  );
};
